import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Spinner, Alert, Badge, Button, Form, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'sonner';
import dayjs from 'dayjs';

import {
    RefreshCw,
    Inbox as InboxIcon,
    Search as SearchIcon,
    AlertTriangle,
    CheckCircle,
    Grid3x3,
    List,
    FileText,
    Paperclip,
    Mail,
} from 'lucide-react';

import { brandSelectTheme } from '../../Common/reactSelectTheme';

import { listInbox, syncInbox, setInboxItemDone, fetchInboxAttachmentThumbnail } from '../../../../services/InboxService';
import { useInbox } from '../../../../context/InboxContext';
import PageTitle from '../../../layouts/PageTitle';
import InboxDetail from './InboxDetail';

/**
 * Inbox — unified intake page. Lists all incoming supplier-invoice
 * candidates from every channel (Mailgun forward, Gmail/Outlook poll,
 * manual upload) and lets the user move them through the workflow.
 *
 * Layout: filter chips on top, split pane below — list on the left,
 * detail panel on the right for the selected row. On narrower
 * viewports the detail panel collapses below the list (CSS handles
 * the wrap; no JS breakpoint logic).
 *
 * Why no virtualization: a tenant's inbox shouldn't have more than a
 * few hundred non-terminal items at any moment. We keep the markup
 * simple until that assumption breaks.
 */

// Filter chips. `states` is what we send to the backend. The default
// "active" view excludes terminal states so the page reads as a to-do list.
// `all` uses the wildcard so deep-links from approval emails can find
// items regardless of their current state.
// `done` controls the reversible mark-as-done filter per chip:
//   'exclude' hides done items (to-do views), 'only' shows just done
//   ("Klara"), 'all' shows both ("Alla").
const FILTER_CHIPS = [
    { key: 'active', label: 'Aktiva', states: ['new', 'processing', 'awaiting_approval', 'error'], metricKeys: ['new', 'processing', 'awaiting_approval', 'error'], done: 'exclude' },
    { key: 'new', label: 'Nya', states: ['new'], metricKeys: ['new'], done: 'exclude' },
    { key: 'awaiting_approval', label: 'Väntar godkännande', states: ['awaiting_approval'], metricKeys: ['awaiting_approval'], done: 'exclude' },
    { key: 'error', label: 'Fel', states: ['error'], metricKeys: ['error'], done: 'exclude' },
    { key: 'linked', label: 'Hanterade', states: ['linked'], metricKeys: ['linked'], done: 'exclude' },
    { key: 'dismissed', label: 'Avfärdade', states: ['dismissed'], metricKeys: ['dismissed'], done: 'exclude' },
    { key: 'done', label: 'Klara', states: ['*'], metricKeys: ['done'], done: 'only' },
    { key: 'all', label: 'Alla', states: ['*'], metricKeys: ['total'], done: 'all' },
];

// List ordering options. `sort` maps to a whitelisted backend column and
// `dir` to ASC/DESC. Default is newest-first.
const SORT_OPTIONS = [
    { key: 'date_desc', label: 'Senaste först', sort: 'received_at', dir: 'desc' },
    { key: 'date_asc', label: 'Äldst först', sort: 'received_at', dir: 'asc' },
    { key: 'sender_asc', label: 'Avsändare (A–Ö)', sort: 'sender_email', dir: 'asc' },
    { key: 'sender_desc', label: 'Avsändare (Ö–A)', sort: 'sender_email', dir: 'desc' },
    { key: 'subject_asc', label: 'Ämne (A–Ö)', sort: 'subject', dir: 'asc' },
    { key: 'status', label: 'Status', sort: 'state', dir: 'asc' },
];

const STATE_BADGE = {
    new: { variant: 'primary', label: 'Ny' },
    processing: { variant: 'info', label: 'Bearbetas' },
    awaiting_approval: { variant: 'warning', label: 'Väntar godkännande' },
    linked: { variant: 'success', label: 'Hanterad' },
    dismissed: { variant: 'secondary', label: 'Avfärdad' },
    error: { variant: 'danger', label: 'Fel' },
};

function StateBadge({ state }) {
    const cfg = STATE_BADGE[state] || { variant: 'secondary', label: state };
    return <Badge bg={cfg.variant}>{cfg.label}</Badge>;
}

function formatDate(value) {
    if (!value) return '—';
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : value;
}

// Sender display name: prefer the "Name" part of "Name <addr>", else the address.
function senderName(sender) {
    if (!sender) return '—';
    const m = sender.match(/^\s*"?([^"<]+?)"?\s*</);
    if (m && m[1].trim()) return m[1].trim();
    return sender;
}

// 1–2 letter initials for the placeholder avatar (no-preview cards).
function initialsOf(name) {
    const parts = (name || '').replace(/[<>"]/g, '').trim().split(/[\s@.]+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
}

// OCR total → localized currency string (e.g. "4 812 kr"). Returns '' if absent.
function formatAmount(amount, currency) {
    if (amount == null || amount === '' || Number.isNaN(Number(amount))) return '';
    try {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: currency || 'SEK',
            maximumFractionDigits: 0,
        }).format(Number(amount));
    } catch (_) {
        return `${amount} ${currency || 'SEK'}`;
    }
}

// Compact ISO date for the card meta row. Locale-free on purpose.
function formatShortDate(value) {
    if (!value) return '';
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD') : '';
}

// Defer the thumbnail fetch until the card scrolls near the viewport.
// Returns [ref, visible]. Falls back to eager when IO is unavailable.
function useLazyVisible(rootMargin = '300px') {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (!ref.current || typeof IntersectionObserver === 'undefined') {
            setVisible(true);
            return undefined;
        }
        const io = new IntersectionObserver((entries) => {
            for (const e of entries) {
                if (e.isIntersecting) { setVisible(true); io.disconnect(); return; }
            }
        }, { rootMargin });
        io.observe(ref.current);
        return () => io.disconnect();
    }, [rootMargin]);
    return [ref, visible];
}

// Session-scoped cache of attachment thumbnails so scrolling / re-rendering /
// switching views doesn't refetch. Value is a blob: URL, or the sentinel
// 'none' for attachments the backend says aren't previewable. The inbox is
// bounded (a few hundred items), so we let the object URLs live for the
// session rather than churn them.
const thumbCache = new Map(); // attachmentId → blobURL | 'none'

// Fetch (once) and cache the first attachment's thumbnail. Status drives the
// card's preview/skeleton/placeholder rendering.
function useThumbnail(companyId, attachmentId, enabled) {
    const cached = attachmentId ? thumbCache.get(attachmentId) : undefined;
    const [url, setUrl] = useState(cached && cached !== 'none' ? cached : null);
    const [status, setStatus] = useState(() => {
        if (!attachmentId) return 'none';
        if (cached === 'none') return 'none';
        if (cached) return 'ready';
        return 'idle';
    });

    useEffect(() => {
        if (!enabled || !attachmentId) return undefined;
        const hit = thumbCache.get(attachmentId);
        if (hit) {
            if (hit === 'none') { setStatus('none'); }
            else { setUrl(hit); setStatus('ready'); }
            return undefined;
        }
        let cancelled = false;
        setStatus('loading');
        fetchInboxAttachmentThumbnail(companyId, attachmentId)
            .then((objectUrl) => {
                if (cancelled) { if (objectUrl) URL.revokeObjectURL(objectUrl); return; }
                if (objectUrl) { thumbCache.set(attachmentId, objectUrl); setUrl(objectUrl); setStatus('ready'); }
                else { thumbCache.set(attachmentId, 'none'); setStatus('none'); }
            })
            .catch(() => { if (!cancelled) setStatus('error'); });
        return () => { cancelled = true; };
    }, [companyId, attachmentId, enabled]);

    return { url, status };
}

// One inbox item as a compact list row (default "list" view).
function InboxRow({ item, isSelected, onSelect }) {
    return (
        <button
            type="button"
            onClick={onSelect}
            className="inbox-list-item"
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderBottom: '1px solid #f3f4f6',
                borderLeft: isSelected ? '3px solid var(--primary)' : '3px solid transparent',
                background: isSelected ? 'var(--rgba-primary-1)' : 'transparent',
                padding: '12px 16px',
                cursor: 'pointer',
            }}
        >
            {/* Row 1 — sender + date */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {item.done_at && (
                    <CheckCircle size={13} title="Klar" style={{ color: 'var(--primary)', flexShrink: 0 }} />
                )}
                <span
                    style={{ fontWeight: 600, fontSize: 13, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={item.sender_email || ''}
                >
                    {senderName(item.sender_email)}
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {formatDate(item.received_at)}
                </span>
            </div>
            {/* Row 2 — subject + status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <span
                    style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={item.subject || ''}
                >
                    {item.subject || '(inget ämne)'}
                </span>
                <span style={{ marginLeft: 'auto', flexShrink: 0 }}>
                    <StateBadge state={item.state} />
                </span>
            </div>
        </button>
    );
}

// The inbox item as a document-preview card (the "grid" view). The hero is a
// real thumbnail of the attached invoice (PDF first page / image), lazily
// fetched and cached. OCR-extracted supplier / amount / invoice-no are
// surfaced on the tile so the user reads the essentials without opening it.
function InboxCard({ companyId, item, isSelected, onSelect, onToggleDone }) {
    const [ref, visible] = useLazyVisible();

    const attachmentIds = Array.isArray(item.attachment_ids) ? item.attachment_ids : [];
    const firstId = attachmentIds[0] || null;
    const extraCount = attachmentIds.length;
    const { url, status } = useThumbnail(companyId, firstId, visible);

    const ocr = item.ocr_fields || {};
    const supplier = ocr.supplierName || senderName(item.sender_email);
    const subtitle = ocr.invoiceNumber ? `Faktura ${ocr.invoiceNumber}` : (item.subject || '(inget ämne)');
    const amount = formatAmount(ocr.totalAmount, ocr.currency);
    const dateLabel = ocr.dueDate
        ? `Förf. ${formatShortDate(ocr.dueDate)}`
        : formatShortDate(item.received_at);

    const showPreview = status === 'ready' && url;
    const showSkeleton = status === 'idle' || status === 'loading';

    return (
        <div
            ref={ref}
            className={`inbox-card${isSelected ? ' selected' : ''}${item.done_at ? ' is-done' : ''}`}
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
            title={item.subject || ''}
        >
            <div className="inbox-card-preview-wrap">
                {/* Stacked-paper effect hints at multiple attachments. */}
                {attachmentIds.length > 2 && <span className="inbox-card-sheet sheet2" />}
                {attachmentIds.length > 1 && <span className="inbox-card-sheet sheet1" />}

                <div className="inbox-card-preview">
                    {/* Overlays: state pill + done check. */}
                    <div className="inbox-card-overlay">
                        <StateBadge state={item.state} />
                        {item.done_at && (
                            <span className="inbox-card-done" title="Klar"><CheckCircle size={16} /></span>
                        )}
                    </div>

                    {showPreview ? (
                        <img className="inbox-card-img" src={url} alt="" loading="lazy" />
                    ) : showSkeleton ? (
                        <div className="inbox-card-skeleton" />
                    ) : (
                        // No preview available (body-only email, or non-previewable
                        // type) — a tasteful placeholder, not an empty box.
                        <div className="inbox-card-placeholder">
                            <span className="inbox-card-initials">{initialsOf(supplier)}</span>
                            {firstId
                                ? <FileText size={20} className="inbox-card-ph-icon" />
                                : <Mail size={20} className="inbox-card-ph-icon" />}
                        </div>
                    )}

                    {attachmentIds.length > 1 && (
                        <span className="inbox-card-attach" title={`${extraCount} bilagor`}>
                            <Paperclip size={11} /> {extraCount}
                        </span>
                    )}

                    {/* Hover quick action — reversible done toggle. */}
                    <button
                        type="button"
                        className="inbox-card-quick"
                        title={item.done_at ? 'Återställ' : 'Markera klar'}
                        onClick={(e) => { e.stopPropagation(); onToggleDone(item); }}
                    >
                        <CheckCircle size={15} />
                    </button>
                </div>
            </div>

            <div className="inbox-card-body">
                <div className="inbox-card-supplier" title={supplier}>{supplier}</div>
                <div className="inbox-card-sub" title={subtitle}>{subtitle}</div>
                <div className="inbox-card-meta">
                    {amount
                        ? <span className="inbox-card-amount">{amount}</span>
                        : <span />}
                    <span className="inbox-card-date">{dateLabel}</span>
                </div>
            </div>
        </div>
    );
}

export default function InboxPage() {
    const { companyId } = useParams();
    const { metrics, refreshMetrics } = useInbox();
    const location = useLocation();
    const history = useHistory();

    // Deep-link target from approval emails: ?item=<inbox_item_id>. When
    // present we widen the filter to 'all' so the row is found regardless
    // of state, then auto-select and scroll the row into view.
    const deepLinkItemId = useMemo(() => {
        const p = new URLSearchParams(location.search);
        return p.get('item');
    }, [location.search]);

    const [activeFilter, setActiveFilter] = useState(deepLinkItemId ? 'all' : 'active');
    // Free-text search. `searchInput` is the raw box value; `debouncedQ` is
    // what we actually send to the backend + mirror into the URL (?q=) so a
    // filtered view is shareable. Seeded from the URL on mount.
    const [searchInput, setSearchInput] = useState(
        () => new URLSearchParams(location.search).get('q') || '',
    );
    const [debouncedQ, setDebouncedQ] = useState(searchInput);
    const [sortKey, setSortKey] = useState('date_desc');
    // How the list of items is rendered: 'list' (compact rows) or 'grid'
    // (card tiles). Mirrors the Documents page toggle; persisted so the
    // user's choice survives reloads.
    const [viewMode, setViewMode] = useState(
        () => localStorage.getItem('inboxViewMode') || 'list',
    );
    const changeView = useCallback((m) => {
        setViewMode(m);
        localStorage.setItem('inboxViewMode', m);
    }, []);
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [error, setError] = useState(null);
    const [selectedId, setSelectedId] = useState(deepLinkItemId || null);

    const activeChip = useMemo(
        () => FILTER_CHIPS.find((c) => c.key === activeFilter) || FILTER_CHIPS[0],
        [activeFilter],
    );

    const activeSort = useMemo(
        () => SORT_OPTIONS.find((o) => o.key === sortKey) || SORT_OPTIONS[0],
        [sortKey],
    );

    const loadItems = useCallback(async () => {
        if (!companyId) return;
        try {
            setIsLoading(true);
            const data = await listInbox(companyId, {
                states: activeChip.states,
                q: debouncedQ || undefined,
                done: activeChip.done && activeChip.done !== 'all' ? activeChip.done : undefined,
                sort: activeSort.sort,
                dir: activeSort.dir,
                limit: 100,
            });
            setItems(data?.items || []);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.error || err.message || 'Kunde inte hämta inkorgen');
        } finally {
            setIsLoading(false);
        }
    }, [companyId, activeChip, debouncedQ, activeSort]);

    useEffect(() => {
        loadItems();
    }, [loadItems]);

    // Debounce the raw search box into the value we query with.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(searchInput.trim()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    // Mirror the active search term into the URL (?q=…) so the view is
    // shareable, without clobbering other params (e.g. the deep-link strip).
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (debouncedQ) params.set('q', debouncedQ);
        else params.delete('q');
        const remaining = params.toString();
        history.replace({
            pathname: location.pathname,
            search: remaining ? `?${remaining}` : '',
            hash: location.hash,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQ]);

    // After a deep-link load, strip ?item= from the URL so refreshes
    // don't keep re-applying the override (e.g. if the user manually
    // switches filters away). We keep `selectedId` so the detail panel
    // stays open; the user can close it explicitly.
    useEffect(() => {
        if (!deepLinkItemId) return;
        const params = new URLSearchParams(location.search);
        params.delete('item');
        const remaining = params.toString();
        history.replace({
            pathname: location.pathname,
            search: remaining ? `?${remaining}` : '',
            hash: location.hash,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLinkItemId]);

    // When the user takes an action on an item (dismiss/retry/etc.) the
    // detail panel triggers this so list + sidebar badge stay in sync.
    const handleItemMutated = useCallback(() => {
        loadItems();
        refreshMetrics();
    }, [loadItems, refreshMetrics]);

    // Grid card hover quick-action: flip the reversible "mark as done" flag
    // without opening the item. Refreshes the list + badge on success.
    const handleToggleDone = useCallback(async (item) => {
        if (!companyId || !item) return;
        try {
            await setInboxItemDone(companyId, item.id, !item.done_at);
            handleItemMutated();
        } catch (err) {
            toast.error(err?.response?.data?.error || err.message || 'Kunde inte uppdatera objektet');
        }
    }, [companyId, handleItemMutated]);

    // "Synka inkorgen" — backend-side on-demand poll of all connected
    // email mailboxes. Surfaces a toast with the result so the user
    // knows whether anything actually changed, then refreshes the list
    // + sidebar badge to show the new items. Independent of the
    // Function's scheduled polling.
    const handleSync = useCallback(async () => {
        if (!companyId) return;
        setIsSyncing(true);
        try {
            const summary = await syncInbox(companyId);
            const { connections_polled, items_created, errors } = summary;
            if (errors && errors.length > 0) {
                // Mixed outcome — surface the first error's reason so the
                // user has something actionable. Backend logs have the
                // full detail; the toast just needs to point in the
                // right direction.
                const first = errors[0];
                toast.warning(
                    `Synkning klar: ${items_created} nya, ${errors.length} anslutning(ar) misslyckades. ` +
                    `Första felet: ${first.message || first.code || 'okänt'}`,
                    { duration: 10000 }
                );
            } else if (items_created > 0) {
                toast.success(`${items_created} nya objekt hämtade från ${connections_polled} anslutning(ar).`);
            } else if (connections_polled === 0) {
                toast.info('Inga aktiva e-postanslutningar att synka.');
            } else {
                toast.info('Inga nya meddelanden hittades.');
            }
        } catch (err) {
            const msg = err?.response?.data?.error || err.message || 'Synkningen misslyckades';
            toast.error(msg);
        } finally {
            setIsSyncing(false);
            // Always refresh the view + badge after a sync attempt so
            // any items that DID land are visible immediately.
            loadItems();
            refreshMetrics();
        }
    }, [companyId, loadItems, refreshMetrics]);

    const selectedItem = useMemo(
        () => items.find((it) => it.id === selectedId) || null,
        [items, selectedId],
    );

    // The detail panel renders inline (split pane) in list view and as a
    // modal in grid view.
    const showSplitDetail = viewMode === 'list' && !!selectedItem;
    const showModalDetail = viewMode === 'grid' && !!selectedItem;

    return (
        <>
            <PageTitle activeMenu="Inkorg" motherMenu="Bokföring" />

            <div className="inbox-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Header row: title + refresh */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <InboxIcon size={28} />
                        <div>
                            <h3 style={{ margin: 0 }}>Inkorg</h3>
                            <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
                                Inkommande leverantörsfakturor från alla källor — e-post, vidarebefordran, uppladdning.
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSync}
                        disabled={isSyncing}
                        title="Hämta nya fakturor från anslutna e-postkonton"
                    >
                        {isSyncing
                            ? <Spinner animation="border" size="sm" />
                            : <RefreshCw size={14} />}
                        <span className="ms-1">{isSyncing ? 'Synkar…' : 'Synka inkorgen'}</span>
                    </Button>
                </div>

                {/* Free-text search — sender, subject, snippet, and OCR
                    supplier name / invoice number. Debounced; narrows within
                    the active filter. Sits above the filter pills (mirrors
                    Midday's inbox search placement). */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Form.Control
                        type="search"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Sök avsändare, ämne, fakturanr…"
                        aria-label="Sök i inkorgen"
                        style={{ flex: '1 1 280px', maxWidth: 420 }}
                    />
                    <div style={{ minWidth: 200 }}>
                        <Select
                            options={SORT_OPTIONS.map((o) => ({ value: o.key, label: o.label }))}
                            value={{ value: activeSort.key, label: activeSort.label }}
                            onChange={(opt) => setSortKey(opt?.value || 'date_desc')}
                            isSearchable={false}
                            theme={brandSelectTheme}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                            styles={{
                                control: (base) => ({ ...base, minHeight: 38, fontSize: 13 }),
                                option: (base) => ({ ...base, fontSize: 13 }),
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            aria-label="Sortera inkorgen"
                        />
                    </div>
                    {/* List / grid view toggle — mirrors the Documents page. */}
                    <div className="inbox-view-toggle" role="group" aria-label="Vy">
                        <button
                            type="button"
                            className={`inbox-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => changeView('list')}
                            title="Lista"
                            aria-label="Lista"
                            aria-pressed={viewMode === 'list'}
                        >
                            <List size={16} />
                        </button>
                        <button
                            type="button"
                            className={`inbox-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => changeView('grid')}
                            title="Rutnät"
                            aria-label="Rutnät"
                            aria-pressed={viewMode === 'grid'}
                        >
                            <Grid3x3 size={16} />
                        </button>
                    </div>
                </div>

                {/* Filter chips with counts */}
                <div className="inbox-filter-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    {FILTER_CHIPS.map((chip) => {
                        const count = chip.metricKeys.reduce((sum, k) => sum + (metrics[k] || 0), 0);
                        const isActive = chip.key === activeFilter;
                        return (
                            <button
                                key={chip.key}
                                type="button"
                                onClick={() => { setActiveFilter(chip.key); setSelectedId(null); }}
                                className="inbox-chip"
                                style={{
                                    border: isActive ? '1px solid var(--primary)' : '1px solid #e5e7eb',
                                    background: isActive ? 'var(--rgba-primary-1)' : '#fff',
                                    color: isActive ? 'var(--primary)' : '#374151',
                                    padding: '6px 12px',
                                    borderRadius: 999,
                                    fontSize: 13,
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                {chip.label}
                                <span style={{
                                    background: isActive ? 'var(--rgba-primary-2)' : '#f3f4f6',
                                    color: isActive ? 'var(--primary-dark)' : '#6b7280',
                                    padding: '0 8px',
                                    borderRadius: 999,
                                    fontSize: 11,
                                    minWidth: 20,
                                    textAlign: 'center',
                                }}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {error && (
                    <Alert variant="danger" className="mb-0">
                        <AlertTriangle size={14} className="me-2" />
                        {error}
                    </Alert>
                )}

                {/* Body. In list view the detail opens as a right-hand split
                    pane. In grid view the grid keeps full width and the detail
                    opens as a modal (below) so the document wall isn't squeezed. */}
                <div className="inbox-split" style={{
                    display: 'grid',
                    // Split only in list view, and only once something is selected
                    // (so we never show an empty right pane).
                    gridTemplateColumns: showSplitDetail ? 'minmax(420px, 1fr) minmax(420px, 1fr)' : '1fr',
                    gap: 16,
                    alignItems: 'start',
                }}>
                    {/* List — rendered as compact rows ('list') or document
                        cards ('grid') per the view toggle. Loading and empty
                        states share the same bordered container regardless. */}
                    <div className="inbox-list-col">
                        {isLoading && items.length === 0 ? (
                            <div className="inbox-state-box" style={{ padding: 40, textAlign: 'center' }}>
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">Laddar inkorgen…</span>
                            </div>
                        ) : items.length === 0 ? (
                            <div className="inbox-state-box" style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                                <SearchIcon size={32} style={{ opacity: 0.4 }} />
                                <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 500 }}>
                                    {debouncedQ ? 'Inga träffar' : 'Inga objekt i denna vy'}
                                </p>
                                <p style={{ fontSize: 13 }}>
                                    {debouncedQ
                                        ? `Inga objekt matchar "${debouncedQ}" i denna vy.`
                                        : activeFilter === 'active'
                                            ? 'Det finns inga obearbetade fakturor just nu.'
                                            : 'Försök byta filter för att se andra objekt.'}
                                </p>
                            </div>
                        ) : viewMode === 'grid' ? (
                            <div className="inbox-grid">
                                {items.map((item) => (
                                    <InboxCard
                                        key={item.id}
                                        companyId={companyId}
                                        item={item}
                                        isSelected={item.id === selectedId}
                                        onSelect={() => setSelectedId(item.id)}
                                        onToggleDone={handleToggleDone}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="inbox-list-box">
                                {items.map((item) => (
                                    <InboxRow
                                        key={item.id}
                                        item={item}
                                        isSelected={item.id === selectedId}
                                        onSelect={() => setSelectedId(item.id)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Detail — split pane (list view only). */}
                    {showSplitDetail && (
                        <InboxDetail
                            companyId={companyId}
                            item={selectedItem}
                            onClose={() => setSelectedId(null)}
                            onMutated={handleItemMutated}
                        />
                    )}
                </div>
            </div>

            {/* Detail — modal (grid view), so the document wall keeps full width. */}
            <Modal
                show={showModalDetail}
                onHide={() => setSelectedId(null)}
                size="xl"
                centered
                dialogClassName="inbox-detail-modal"
                contentClassName="inbox-detail-modal-content"
            >
                {selectedItem && (
                    <InboxDetail
                        companyId={companyId}
                        item={selectedItem}
                        onClose={() => setSelectedId(null)}
                        onMutated={handleItemMutated}
                    />
                )}
            </Modal>

            {/* Co-located styles — modest enough to keep inline. If this
                grows, extract to src/scss/components/_inbox.scss. */}
            <style>{`
                .inbox-list-item:hover { background: #f9fafb; }
                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

                /* View toggle — mirrors .docs-view-toggle on the Documents page. */
                .inbox-view-toggle { display: flex; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; flex-shrink: 0; }
                .inbox-view-btn { width: 36px; height: 38px; border: none; background: #fff; display: flex; align-items: center; justify-content: center; color: #6b7280; cursor: pointer; transition: all 0.15s; }
                .inbox-view-btn + .inbox-view-btn { border-left: 1px solid #e5e7eb; }
                .inbox-view-btn:hover { background: #f9fafb; }
                .inbox-view-btn.active { background: var(--rgba-primary-1); color: var(--primary); }

                /* List view container + shared loading/empty box. */
                .inbox-list-box, .inbox-state-box { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }

                /* ── Grid view — document-preview cards ─────────────────────── */
                .inbox-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 18px; }
                .inbox-card { display: flex; flex-direction: column; background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 10px; cursor: pointer; transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease; outline: none; }
                .inbox-card:hover { border-color: var(--primary); box-shadow: 0 10px 24px rgba(0,0,0,0.10); transform: translateY(-3px); }
                .inbox-card:focus-visible { border-color: var(--primary); box-shadow: 0 0 0 3px var(--rgba-primary-1); }
                .inbox-card.selected { border-color: var(--primary); box-shadow: 0 0 0 2px var(--rgba-primary-2); }
                .inbox-card.is-done { opacity: 0.78; }

                /* Preview hero + the stacked-paper layers behind it. */
                .inbox-card-preview-wrap { position: relative; margin: 2px 6px 0 2px; }
                .inbox-card-sheet { position: absolute; inset: 0; background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; }
                .inbox-card-sheet.sheet1 { transform: translate(3px, -3px); }
                .inbox-card-sheet.sheet2 { transform: translate(6px, -6px); }
                .inbox-card-preview { position: relative; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; aspect-ratio: 3 / 4; background: #f9fafb; }
                .inbox-card-img { width: 100%; height: 100%; object-fit: cover; object-position: top center; display: block; transition: transform 0.25s ease; }
                .inbox-card:hover .inbox-card-img { transform: scale(1.03); }

                /* Loading shimmer. */
                .inbox-card-skeleton { width: 100%; height: 100%; background: linear-gradient(100deg, #f3f4f6 30%, #e9ebee 50%, #f3f4f6 70%); background-size: 200% 100%; animation: inbox-shimmer 1.3s ease-in-out infinite; }
                @keyframes inbox-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }

                /* No-preview placeholder (body-only mail / non-previewable). */
                .inbox-card-placeholder { width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; background: linear-gradient(135deg, var(--rgba-primary-1), #f9fafb); }
                .inbox-card-initials { width: 46px; height: 46px; border-radius: 50%; background: var(--primary); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 700; }
                .inbox-card-ph-icon { color: var(--primary); opacity: 0.7; }

                /* Overlays on the preview. */
                .inbox-card-overlay { position: absolute; top: 8px; left: 8px; right: 8px; display: flex; align-items: flex-start; justify-content: space-between; gap: 6px; pointer-events: none; }
                .inbox-card-overlay .badge { box-shadow: 0 1px 3px rgba(0,0,0,0.25); }
                .inbox-card-done { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 50%; background: #fff; color: var(--primary); box-shadow: 0 1px 4px rgba(0,0,0,0.25); }
                .inbox-card-attach { position: absolute; bottom: 8px; left: 8px; display: inline-flex; align-items: center; gap: 3px; padding: 2px 7px; border-radius: 999px; background: rgba(17,24,39,0.78); color: #fff; font-size: 11px; font-weight: 600; }
                .inbox-card-quick { position: absolute; bottom: 8px; right: 8px; width: 30px; height: 30px; border-radius: 50%; border: none; background: #fff; color: #6b7280; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,0.2); opacity: 0; transform: translateY(4px); transition: all 0.15s; }
                .inbox-card:hover .inbox-card-quick, .inbox-card:focus-within .inbox-card-quick { opacity: 1; transform: translateY(0); }
                .inbox-card-quick:hover { background: var(--primary); color: #fff; }
                .inbox-card.is-done .inbox-card-quick { color: var(--primary); }
                @media (hover: none) { .inbox-card-quick { opacity: 1; transform: none; } }

                /* Card text body. */
                .inbox-card-body { display: flex; flex-direction: column; gap: 2px; padding: 10px 6px 4px; }
                .inbox-card-supplier { font-weight: 600; font-size: 13px; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .inbox-card-sub { font-size: 12px; color: #6b7280; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .inbox-card-meta { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; margin-top: 4px; }
                .inbox-card-amount { font-size: 13px; font-weight: 700; color: var(--primary); }
                .inbox-card-date { font-size: 11px; color: #9ca3af; white-space: nowrap; margin-left: auto; }

                /* Grid-view detail modal — let InboxDetail supply its own card
                   chrome; the modal only provides width + backdrop. */
                .inbox-detail-modal { max-width: 1100px; width: 96vw; }
                .inbox-detail-modal-content { border: none; background: transparent; box-shadow: none; }
            `}</style>
        </>
    );
}
